/*
 * Shadow-root shim for the Adoric Bundles widgets (`bundle-adoric`,
 * `bundle-adoric-mnm`).
 *
 * SCOPE: deliberately tiny. Colours and sizes are NOT here - they are set as
 * --adoric-* variable overrides in Adoric's own GLOBAL custom-CSS field, which
 * reaches them because the host is a light-DOM element.
 *
 * This file exists because Adoric injects that CSS into the PAGE HEAD, so it
 * cannot style anything INSIDE the widget: CSS does not cross a shadow
 * boundary. Measured 2026-08-27 with only Adoric's CSS active - every rule below
 * failed, the quantity pickers returning to 30px and the total's prices to the
 * app's stock red and blue.
 *
 * The shadow root is OPEN, so these rules are injected into it directly. That is
 * the only route for anything selecting inside the widget.
 *
 * THIRD-PARTY SELECTOR WARNING
 * .quantity-picker, .preorder-banner, .add-to-cart-button, .heading h6 and
 * .product-image img are owned by the Adoric Bundles theme app extension.
 * Captured from the live shadow DOM on 2026-08-27 (.product-image img added
 * 2026-09-11), no published contract, re-verify after an app update. The widget's Svelte classes (svelte-1ox0qrz and friends) are
 * deliberately NOT used: that hash changes on every one of their builds.
 */
(function () {
  'use strict';

  /* Adoric renders each bundle TYPE as its own custom element. Both types we use
     reuse the same internal class names, verified by diffing the two renderer
     bundles on 2026-08-27: .quantity-picker, .preorder-banner,
     .add-to-cart-button, .summary, .compare, .badge and .heading all appear in
     both, and they share 158 --adoric-* variables. So one stylesheet serves
     both and this list is the only thing that needs extending for a new type.
       mark-two   = Product Bundles (BUNDLE_TYPE 1)
       mark-seven = Mix and Match   (BUNDLE_TYPE 6) */
  var HOSTS = ['adoric-bundle-mark-two', 'adoric-bundle-mark-seven'];
  var MARK = 'data-ml-adoric';

  var CSS = [
    /* 0. Product photos: mix-blend-mode darken, the same trick the whole
       site uses (morning-light-global.css) - white photo backgrounds become
       whatever surface is behind them, so the cream cards set in Adoric's
       custom-code field read as bare product shots, not white boxes. The
       site's own rules cannot cross the shadow boundary, hence here. */
    'img{mix-blend-mode:darken;}',
    /* 1 + 2. Type. Adoric exposes size and weight as variables but not family
       or transform, and the eyebrow heading and button both need the condensed
       face to match the rest of the buy box. */
    ".heading h6{font-family:'Archivo Narrow','Archivo',system-ui,sans-serif;",
    'letter-spacing:.12em;text-transform:uppercase;}',
    ".add-to-cart-button{font-family:'Archivo Narrow','Archivo',system-ui,sans-serif;",
    'letter-spacing:1.14px;text-transform:uppercase;',
    /* Radius is set INLINE on the button by the app, with no variable behind
       it, so it cannot be themed from Adoric's own CSS either - measured at
       4.5px against the 10px the EB widget uses. */
    'border-radius:10px !important;}',

    /* 3. The struck total. Adoric exposes a compare-at colour variable for the
       COMPONENT prices (--adoric-products-price-compare-color, set in
       ml-bundle-adoric.css) but has no equivalent for the total row: there is
       no --adoric-total-price-* variable at all, only title/background ones.
       The total's struck price therefore keeps the app's stock #FF5D5D red,
       which belongs to no Morning Light palette and reads as an error state
       next to our coral. Same tint as the component compare. */
    '.summary .price .compare{color:var(--ml-ink-tint) !important;}',

    /* The struck price on a Mix and Match section card. Adoric exposes
       --adoric-products-price-compare-color for the PB component cards, but
       .product-info__compare-price inside an MNM section does not read it - it
       keeps the app's stock #FF5D5D. Measured on the live widget after a real
       selection: the total's struck price was correctly tinted while the card's
       was still red, which is the sort of thing only a screenshot catches.
       No variable reaches it, so it has to be done here. */
    '.product-info__compare-price{color:var(--ml-ink-tint) !important;}',

    /* The savings pill on the TOTAL row. --adoric-products-badge-color-* covers
       the per-component pills only; the total's pill has no variable of its own
       and keeps the app's stock blue (#D6E7FB on #006CE7), which is the one
       remaining off-palette element once everything else is themed. */
    '.summary .badge{background:var(--butter) !important;color:var(--ink) !important;}',

    /* 4. Component quantity pickers, hidden deliberately.
       A bundle is bought as a set. Letting a shopper change one component's
       count on the PDP splits the set, and it is the surface that leaks the
       bundle discount onto extra units (measured: an extra strap took 5.9674%
       off, the bundle's own rate).
       This is suppressed here rather than switched off in Adoric because the
       app setting that draws these pickers, "Allow user to choose more items"
       (flexibleQuantity), is the SAME setting that makes the discount apply to
       a second bundle. Turning it off would restore the multi-bundle
       overcharge. Two bundles is still two clicks of Add to Cart. */
    '.quantity-picker{display:none !important;}',

    /* 5. Adoric's preorder banner, suppressed for the same reason the EB shim
       hides .gbbPbPreOrderPill: the theme already renders its own preorder
       disclosure above the buy box from ml-bundle-preorder.liquid, and that one
       carries the REAL ship date from the Restock Rocket metafield
       ("Est. Ship: 9/7"). Adoric's reads "based on the estimated delivery rate"
       with no date, so showing both means two disclosures, the vaguer one
       second. The per-component "Preorder" LABEL is kept - it marks WHICH
       component is on preorder, which our single notice cannot say. */
    '.preorder-banner{display:none !important;}',

    /* Mix and Match section progress bars, hidden. With two sections and an
       explicit Add button per item, a progress indicator tells the shopper
       nothing the sections do not already show, and at full width it rendered
       as two solid rules competing with the primary Add to Cart for attention.
       Adoric has no variable to disable them, only to colour them. */
    '.section__progress-bar, .progress-bar{display:none !important;}'
  ].join('');

  /* Product Bundles only (mark-two): borderless cards joined by an ink "+"
     (Seth 2026-08-28 - with the photo blend live, the borders were the last
     boxed chrome; two floating hats joined by a plus reads as an equation).
     The border itself is zeroed in the Adoric-admin custom-code field, per the
     variables-there / shadow-rules-here split. Adoric's own plus-divider
     variable never renders in the grid layout, so the mark is drawn here.
     NOT applied to Mix and Match (mark-seven): its section panels are
     interactive pickers and keep their borders for structure. */
  var CSS_PB = [
    '.product{position:relative;overflow:visible;}',
    '.bundle-products .product:not(:last-child)::after{',
    "content:'+';position:absolute;top:130px;right:-19px;width:38px;",
    'text-align:center;font-size:34px;font-weight:700;line-height:1;',
    'color:#2b211b;z-index:2;}',
    /* The grid stays two-up even at 375px (measured, never stacks), so the
       mobile case is only a smaller mark higher on the image whitespace. */
    '@media (max-width:767px){',
    '.bundle-products .product:not(:last-child)::after{',
    'top:70px;right:-13px;width:26px;font-size:22px;}',
    '}'
  ].join('');

  function paint(host) {
    var root = host && host.shadowRoot;
    if (!root || root.querySelector('style[' + MARK + ']')) return;
    var style = document.createElement('style');
    style.setAttribute(MARK, '');
    style.textContent = CSS;
    if (host.tagName.toLowerCase() === 'adoric-bundle-mark-two') {
      style.textContent += CSS_PB;
    }
    root.appendChild(style);
  }

  /* Adoric's renderer writes a bare src with no width= and no srcset, so the
     browser is handed the full-size Shopify original (measured 2049px on
     2026-09-11) and downscales it into a box around 250px wide. A downscale
     that large aliases, and on the fine denim weave of the hats it shows as a
     wavy diagonal moire across the thumbnail.

     A fixed width= is not enough, which is why the first pass did not fix it.
     width=600 into a 266 css px box is 532 device px on a 2x screen, so the
     browser still resamples 600 down to 532 (and down to 266 at 1x) and the
     weave still beats against the pixel grid. The gallery is clean for exactly
     the opposite reason: its srcset and sizes hand the browser an image at the
     size it is painted, so there is no browser resample at all.

     So the target width is measured per image from its own rendered box times
     the device pixel ratio, and a srcset carrying 1x and 2x density
     descriptors is written alongside it so the browser can pick an exact-size
     candidate instead of resampling. Widths snap UP to the nearest multiple of
     10 so the CDN caches a handful of sizes rather than one per viewport.
     Only cdn.shopify.com URLs are touched, because the width parameter is a
     Shopify CDN feature and means nothing on any other host. */
  var MIN_W = 200;
  var MAX_W = 2048;
  /* A resize only earns a refetch when the box really moved. Anything inside
     this fraction keeps the image the node already has. */
  var RESIZE_TOLERANCE = 0.1;

  function snapWidth(px) {
    var w = Math.ceil(px / 10) * 10;
    if (w < MIN_W) w = MIN_W;
    if (w > MAX_W) w = MAX_W;
    return w;
  }

  /* The density descriptors to offer, always including the current screen's.
     Rounded to one decimal so an odd ratio (1.7647 on a scaled Windows laptop)
     does not produce an unreadable descriptor or defeat the snapped widths. */
  function densities(dpr) {
    var list = [1, 2];
    var d = Math.round(dpr * 10) / 10;
    if (d > 0 && list.indexOf(d) === -1) list.push(d);
    return list.sort(function (a, b) { return a - b; });
  }

  /* These URLs already carry ?v=..., but do not assume it. */
  function withWidth(base, w) {
    return base + (base.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function sizeImages(host) {
    var root = host && host.shadowRoot;
    if (!root) return;
    var imgs = root.querySelectorAll('.product-image img');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      var src = img.getAttribute('src') || '';
      /* Our own write changes the src, so record the URL we settled on and use
         it to tell our URL apart from a fresh one Adoric just wrote: without
         this the pass would rewrite the same node on every observer wake-up and
         refetch the image each time. The marker stores the URL rather than a
         bare flag because Adoric reuses image nodes across variant and product
         changes - a permanent flag would leave a recycled node pinned to the
         full-size URL it was just given. The un-widthed URL is kept beside it
         so a resize can recompute without having to unpick our own parameter. */
      var settled = src !== '' && img.getAttribute('data-ml-sized') === src;
      /* A src that is not the one we last wrote is a fresh URL from Adoric on a
         recycled node, so everything we previously hung on that node describes
         the PREVIOUS image. The srcset especially: candidates win over src, so
         leaving one behind pins the node to the old photo no matter what src
         says. Clear the lot before deciding what to do with the new URL, so
         every early return below leaves Adoric's own src in charge. */
      if (!settled) {
        img.removeAttribute('srcset');
        img.removeAttribute('data-ml-base');
        img.removeAttribute('data-ml-w');
        img.removeAttribute('data-ml-dpr');
        img.removeAttribute('data-ml-sized');
      }
      var base = settled ? (img.getAttribute('data-ml-base') || '') : src;
      if (!base) continue;
      if (base.indexOf('cdn.shopify.com') === -1) continue;
      /* A src that already carries a width is Adoric's own choice, not ours, so
         leave it alone and stop looking at the node. */
      if (!settled && base.indexOf('width=') !== -1) {
        img.setAttribute('data-ml-sized', src);
        continue;
      }

      var cssWidth = img.getBoundingClientRect().width;
      /* Not laid out yet (a display:none ancestor, or measured before the
         widget has sized its grid). Fall back to the old fixed width so the
         node is never left at full size, and leave the marker unset so the next
         observer or resize pass measures it properly. */
      if (!cssWidth) {
        if (!settled) {
          var fallback = withWidth(base, 600);
          img.setAttribute('src', fallback);
          img.setAttribute('data-ml-base', base);
          /* Marked so the node is recognised as ours next pass, but with no
             recorded width, so the tolerance check cannot short-circuit the
             real measurement once the box exists. */
          img.setAttribute('data-ml-sized', fallback);
        }
        continue;
      }

      var dpr = Math.round((window.devicePixelRatio || 1) * 10) / 10;
      /* The RENDERED width is what gets cached and compared, never the snapped
         one: snapWidth clamps at 200, so on a thumbnail below that every size
         would compare equal and a real resize would be skipped. And the ratio
         is half the target, so a window dragged to a denser display needs a
         refetch even when the box did not move at all. */
      var prev = parseFloat(img.getAttribute('data-ml-w')) || 0;
      var prevDpr = parseFloat(img.getAttribute('data-ml-dpr')) || 0;
      if (settled && prev && prevDpr === dpr &&
          Math.abs(cssWidth - prev) / prev <= RESIZE_TOLERANCE) continue;

      var sized = withWidth(base, snapWidth(cssWidth * dpr));
      img.setAttribute('src', sized);
      /* The candidate list must contain THIS screen's density, not just 1x and
         2x: srcset candidates override src, so on a 3x phone a list that stops
         at 2x would hand the browser a 2x image to upscale and undo the whole
         point of the change. 1x and 2x are still offered so the same markup
         stays right if the page moves to another screen. */
      img.setAttribute('srcset', densities(dpr).map(function (d) {
        return withWidth(base, snapWidth(cssWidth * d)) + ' ' + d + 'x';
      }).join(', '));
      img.setAttribute('data-ml-base', base);
      img.setAttribute('data-ml-w', String(cssWidth));
      img.setAttribute('data-ml-dpr', String(dpr));
      img.setAttribute('data-ml-sized', sized);
    }
  }

  /* Adoric re-renders its shadow content on variant and quantity changes, which
     takes our style node with it, so re-append after it settles.
     A TRAILING debounce, not requestAnimationFrame: rAF fires while the widget
     is still rendering, so the write lands first and is overwritten a frame
     later. That exact mistake cost three attempts on the EB shim - see
     ml-bundle-eb.js and the project_bundle_eb_template memory. */
  function watch(host) {
    var timer = null;
    new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(function () { paint(host); sizeImages(host); }, 250);
    /* src is watched as well as childList: Adoric swaps the photo on a variant
       change by writing the attribute on the EXISTING node, which a childList
       observer never sees, so the new full-size URL would go unsized. Our own
       write wakes the observer once more and then settles: the marker now
       matches the src, so the next pass writes nothing. */
    }).observe(host.shadowRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src']
    });
    /* A resize changes the rendered box, and with it the exact-size target, so
       remeasure after it settles. Trailing debounce for the same reason as the
       observer above, and because a drag-resize fires continuously; the
       tolerance check inside sizeImages keeps a small change from refetching. */
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { sizeImages(host); }, 150);
    }, { passive: true });
    paint(host);
    sizeImages(host);
    stickyBar(host);
  }

  /* The element is defined by the app extension, which loads independently of
     this file, so wait for the definition rather than polling. */
  /* Each element is defined by the app extension, which loads independently of
     this file, so wait for the definition rather than polling the DOM. Only the
     type actually on the page ever resolves; the others simply never fire. */
  function start() {
    HOSTS.forEach(function (tag) {
      var existing = document.querySelector(tag);
      if (existing && existing.shadowRoot) {
        watch(existing);
        return;
      }
      if (!window.customElements) return;
      window.customElements.whenDefined(tag).then(function () {
        /* whenDefined resolves as the element upgrades and attaches its shadow
           root, so yield once before reading it. */
        setTimeout(function () {
          var el = document.querySelector(tag);
          if (el && el.shadowRoot) watch(el);
        }, 0);
      });
    });
  }

  /* ---------------------------------------------------------------------
     Sticky Add to Cart.

     The bar is server-rendered by ml-bundle-sticky-atc.liquid so it costs no
     layout shift. This wires three things:
       1. reveal it only once the widget has scrolled out of view, so it never
          covers the buy box it duplicates;
       2. forward its click to Adoric's own Add to Cart, so every path to the
          cart goes through the app and the cart receives COMPONENT lines
          rather than the unfulfillable no-SKU parent;
       3. keep its price in step with the widget's total, which changes as a
          Mix and Match shopper picks different options.
     --------------------------------------------------------------------- */
  function stickyBar(host) {
    var bar = document.querySelector('[data-ml-bundle-sticky]');
    if (!bar) return;
    var button = bar.querySelector('[data-ml-sticky-add]');
    var priceEl = bar.querySelector('[data-ml-sticky-price]');
    var compareEl = bar.querySelector('[data-ml-sticky-compare]');

    function widgetButton() {
      return host.shadowRoot && host.shadowRoot.querySelector('.add-to-cart-button');
    }

    /* Mirror the widget's live total and its disabled state. Mix and Match
       disables its button until every section is satisfied, and a sticky bar
       that looks clickable while the real one is not would be a dead end. */
    function sync() {
      var root = host.shadowRoot;
      if (!root) return;
      var live = root.querySelector('.summary .price [class*="actual"], .summary .price > :last-child');
      var compare = root.querySelector('.summary .price .compare');
      if (live && live.textContent.trim()) priceEl.textContent = live.textContent.trim();
      if (compareEl && compare && compare.textContent.trim()) compareEl.textContent = compare.textContent.trim();
      var wb = widgetButton();
      if (wb) {
        var off = wb.className.indexOf('disabled') >= 0 || wb.disabled;
        if (off) button.setAttribute('disabled', '');
        else button.removeAttribute('disabled');
        button.textContent = wb.textContent.trim() || button.textContent;
      }
    }

    button.addEventListener('click', function () {
      var wb = widgetButton();
      if (wb) wb.click();
    });

    /* Reveal only after the widget has left the viewport UPWARDS, so the bar
       never duplicates a buy box the shopper can already see, and never
       appears before they have scrolled down to it.

       This reads a LIVE rect on scroll rather than using an
       IntersectionObserver entry. An entry's boundingClientRect is a snapshot
       taken when the intersection was computed, not when the callback runs, so
       a callback delivered late carries a stale rect. Measured 2026-08-27: the
       same scroll position reported the bar visible on one run and hidden on
       the next, because the reveal was being decided from the PREVIOUS scroll
       position's rect. A live read cannot go stale.

       Throttled to one read per frame, and the listener is passive, so it
       never blocks scrolling. */
    var queued = false;
    var shown = null;
    function evaluate() {
      queued = false;
      /* bottom < 0 means the whole widget is above the viewport. Using bottom
         rather than top matters on the desktop layout, where the buy column is
         sticky and the widget stays on screen well past its own top edge. */
      var next = host.getBoundingClientRect().bottom < 0;
      if (next === shown) return;
      shown = next;
      if (next) bar.removeAttribute('hidden');
      else bar.setAttribute('hidden', '');
      /* Publish the state so the stylesheet can lift the back-to-top control
         clear of the bar, and the bar's real height so that shift stays correct
         if the title wraps. Measured after the hidden attribute is set, since a
         hidden element has no height. */
      document.body.classList.toggle('ml-bundle-sticky-on', next);
      if (next) {
        document.body.style.setProperty(
          '--ml-bundle-sticky-h', bar.getBoundingClientRect().height + 'px');
      }
    }
    function onScroll() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(evaluate);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    evaluate();

    sync();
    var t = null;
    new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(sync, 250);
    }).observe(host.shadowRoot, { childList: true, subtree: true, characterData: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
